import { useState } from 'react'
import { usePostHog } from '@posthog/react'
import { captureAccountDump } from '../debug/accountDump'
import { t } from '../i18n'
import { errorMessage } from '../api/client'
import { useCustomer } from '../api/queries'
import { useSession } from '../session/SessionContext'
import { Dialog } from '../components/Dialog'
import type { MarketplaceItem } from '../api/types'
import type { PosAccountLine, PosDiscount } from '../lightspeed/types'
import {
  getLightspeedApi,
  addItemAndFindLine,
  hostFunctionNames,
  BADGER_FREE_DISCOUNT_CODE,
} from '../lightspeed/pos-api'
import { FIXTURE_CUSTOMER, FIXTURE_ITEMS, FIXTURE_MENU } from './marketplace-fixtures'
import {
  applyClaim,
  cardStatus,
  formatPoints,
  lineHasDiscount,
  matchingOrderLines,
  titleSizeClass,
  orderForDisplay,
  revokeClaim,
} from './marketplace-model'
import { ClaimConfirmDialog, type ClaimMode } from './ClaimConfirmDialog'
import { RewardLinkDialog } from './RewardLinkDialog'
import { OrderLinePicker } from './OrderLinePicker'
import { KeypadScreen } from './KeypadScreen'
import { useSaleClaims } from './useSaleClaims'

/** Put the reward's discount on one line of the open sale. Throws unless it demonstrably landed.
 *
 * Callers apply this BEFORE moving any points: if the discount does not land, the customer's
 * balance is untouched and the cashier is told — the opposite order would spend points on a bill
 * that never got cheaper. */
async function offerOnLine(item: MarketplaceItem, lineId: string) {
  const ls = getLightspeedApi()
  // The merchant's own code if they linked one, else the conventional BADGER_FREE.
  const code = item.pos_link?.discount_code ?? BADGER_FREE_DISCOUNT_CODE
  await ls.toggleLineDiscount(code, lineId)
  // Re-read rather than trust the toggle: it resolves whenever the host accepts the call, which
  // it does even for a discount code the merchant never created (see `lineHasDiscount`).
  const after = await ls.getCurrentAccount()
  if (!lineHasDiscount(after.lines?.find((l) => l.lineId === lineId))) {
    throw new Error(t('discountDidNotApply'))
  }
}

function ItemCard({
  item,
  balance,
  claimed,
  onClaim,
}: {
  item: MarketplaceItem
  balance: number
  claimed: boolean
  onClaim: (item: MarketplaceItem) => void
}) {
  // Four states: claimable (blue border, colour photo, solid pill), too expensive / locked (no
  // border, desaturated photo, outlined pill + reason), and claimed-this-sale — PENDING.
  //
  // Pending is amber and keeps its colour photo, because it is not a finished or disabled thing:
  // the reward is on the ticket but nobody has paid yet, and the cashier can still undo it. Greying
  // it out (as it was) read as "done, nothing to see", which is the opposite of what the cashier
  // needs to notice mid-service.
  const status = cardStatus(item, balance, claimed)
  const claimable = status === 'claimable'
  const pending = status === 'claimed'
  // Only genuinely unavailable rewards get desaturated — a pending one stays in full colour.
  const dimmed = !claimable && !pending
  const subtitle = {
    claimable: null,
    claimed: t('claimedTapToUndo'),
    locked: t('rewardLocked'),
    unaffordable: t('insufficientPoints'),
  }[status]
  // A claimed card stays tappable so a mis-tap is recoverable in place — tapping it again asks to
  // give the points back. Only "too expensive" and "locked" are genuinely inert.
  const interactive = claimable || status === 'claimed'

  return (
    <button
      type="button"
      disabled={!interactive}
      onClick={() => onClaim(item)}
      aria-label={`${item.label} — ${formatPoints(item.points_cost)} ${t('pts')}${
        status === 'claimed' ? ` — ${t('claimedTapToUndo')}` : ''
      }`}
      // Height comes from the grid row, NOT a fixed 208px. A rigid card inside `grid-rows-2`
      // rows sized to half the container overflows into the row below the moment the viewport is
      // shorter than two cards — the name and price of every card get swallowed by the next one.
      className={`relative flex h-full min-h-0 w-[357px] shrink-0 flex-col overflow-hidden rounded-lg bg-white text-left transition ${
        claimable
          ? 'border-2 border-brand hover:brightness-95 active:scale-[0.985]'
          : pending
            ? 'border-2 border-pending hover:brightness-95 active:scale-[0.985]'
            : 'border-2 border-transparent cursor-not-allowed'
      }`}
    >
      {/* The image absorbs whatever height is left over; the label strip below is fixed, so the
        * name and points stay legible at every viewport height instead of being the first thing
        * squeezed out. At the design's 208px card this lands on the intended 139px. */}
      <div className="relative min-h-0 w-full flex-1 overflow-hidden bg-neutral-200">
        {item.image_url && (
          <img
            src={item.image_url}
            alt=""
            className={`h-full w-full object-cover ${dimmed ? 'grayscale' : ''}`}
          />
        )}
        {dimmed && <div className="absolute inset-0 bg-[#c4c4c4]/45" />}
      </div>

      <div className="flex h-[69px] shrink-0 items-center gap-3 px-[18px]">
        <div className="min-w-0 flex-1">
          <p className={`truncate font-semibold text-ink ${titleSizeClass(item.label)}`}>
            {item.label}
          </p>
          {/* nowrap: "Toucher pour annuler" wrapped onto a second line and pushed the card's
            * text block out of alignment with its neighbours. */}
          {subtitle && (
            <p
              className={`mt-0.5 truncate whitespace-nowrap text-[12px] font-medium ${
                pending ? 'text-pending' : 'text-ink-muted'
              }`}
            >
              {subtitle}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 rounded-full text-center text-[16px] font-semibold ${
            claimable
              ? 'bg-brand px-4 py-[7px] text-white'
              : pending
                ? 'border border-pending bg-pending-50 px-4 py-[6px] text-pending'
                : 'border border-ink-muted px-4 py-[6px] text-ink-muted'
          }`}
        >
          {formatPoints(item.points_cost)} {t('pts')}
        </span>
      </div>
    </button>
  )
}

function FooterButton({
  children,
  onClick,
  variant = 'neutral',
  className = '',
}: {
  children: React.ReactNode
  onClick: () => void
  variant?: 'neutral' | 'brandText' | 'brand'
  className?: string
}) {
  const styles = {
    neutral: 'bg-white border border-line text-ink-soft text-[18px] font-medium',
    brandText: 'bg-white border border-line text-brand text-[19px] font-medium',
    brand: 'bg-brand text-white text-[20px] font-semibold',
  }[variant]
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-16 rounded-lg transition active:scale-[0.98] ${styles} ${className}`}
    >
      {children}
    </button>
  )
}

/** Who the customer is and what they can spend — the two facts the cashier reads first. */
function CustomerBand({ displayName, balance }: { displayName: string; balance: number }) {
  return (
    <>
      <div className="flex shrink-0 items-start justify-between gap-6 px-10 pt-6">
        <div className="min-w-0">
          <p className="truncate text-[40px] font-semibold leading-none text-ink">{displayName}</p>
          <p className="mt-3 text-[17px] text-ink-soft">{t('linkedPointsOnPayment')}</p>
        </div>
        <div className="flex h-[72px] w-[260px] shrink-0 items-center justify-center rounded-lg border border-brand bg-brand-50">
          {/* `key` on the balance restarts the count animation whenever it changes. */}
          <span key={balance} className="point-value text-[34px] font-bold text-brand">
            {formatPoints(balance)} {t('pts')}
          </span>
        </div>
      </div>

      <h2 className="shrink-0 px-10 pt-5 text-[15px] font-semibold tracking-[1.2px] text-ink-soft">
        {t('marketplaceClaimableNow')}
      </h2>
    </>
  )
}

/** The three things a cashier can do that are not claiming a reward. */
function MarketplaceFooter({
  onClose,
  onAddPoints,
  onDone,
}: {
  onClose: () => void
  onAddPoints: () => void
  onDone: () => void
}) {
  return (
    <footer className="flex shrink-0 items-center justify-between gap-4 px-10 pb-9 pt-2">
      <div className="flex gap-2">
        <FooterButton onClick={onClose} className="w-[220px]">
          ✕&nbsp;&nbsp;&nbsp;&nbsp;{t('close')}
        </FooterButton>
      </div>
      <div className="flex gap-2">
        <FooterButton variant="brandText" onClick={onAddPoints} className="w-[271px]">
          ＋ {t('addPoints')}
        </FooterButton>
        <FooterButton variant="brand" onClick={onDone} className="w-[180px]">
          {t('done')}
        </FooterButton>
      </div>
    </footer>
  )
}

/** Ring the reward's product up on the ticket and return the line the POS created.
 *
 * Throws with a cashier-readable reason instead of returning null, so callers cannot forget to
 * check. Reports either way: a silent "nothing happened" is the hardest thing to diagnose from a
 * session replay, since console output is not captured — this is the only channel that says which
 * ids were sent, whether the host even has the function, and what the ticket did. */
async function ringUpForReward(
  posthog: { capture: (event: string, properties?: Record<string, unknown>) => void } | undefined,
  item: MarketplaceItem,
  sku: string,
): Promise<PosAccountLine> {
  // Device evidence (2026-07-29): the internal identifier WORKS and the SKU does not — an add with
  // SKU "2" returned no line at all. So a reward with no internal id cannot be rung up, and naming
  // it beats a generic failure the cashier can do nothing about. The synced menu (TechSpec B4) is
  // what will carry this id for every product; today only items seen on a ticket have one.
  const internal = FIXTURE_MENU.find((m) => m.item_id === sku)?.pos_item_identifier
  if (!internal) throw new Error(t('itemMissingPosId'))
  // Before AND after: the pair distinguishes "the host ignored us" from "it added a line we failed
  // to recognise", which look identical from a single dump.
  await captureAccountDump(posthog, 'before_add', { reward_id: item.reward_id, sku, internal })
  const report = await addItemAndFindLine(getLightspeedApi(), [internal, sku])
  posthog?.capture('webext_add_item', {
    reward_id: item.reward_id,
    sku,
    tried: report.tried,
    has_add_item: report.hasAddItem,
    threw: report.threw,
    lines_before: report.linesBefore,
    lines_after: report.linesAfter,
    added_line_sku: report.line?.itemSku,
    succeeded: !!report.line,
    host_functions: hostFunctionNames(),
  })
  await captureAccountDump(posthog, 'after_add', {
    reward_id: item.reward_id,
    succeeded: !!report.line,
  })
  if (!report.line) throw new Error(t('itemNotAdded'))
  return report.line
}

/** Two rows filled column-major, scrolling sideways — the design's grid exactly (Figma 2346:379);
 * `grid-flow-col` gives the column-major fill for free.
 *
 * Rows are fractions of the available height, capped at the design's 436px (2 × 208 + gap) so the
 * cards keep their intended proportions on a tall screen and simply get shorter — never taller than
 * their row — on a short one. */
function MarketplaceGrid({
  items,
  balance,
  claimed,
  onClaim,
}: {
  items: MarketplaceItem[]
  balance: number
  claimed: ReadonlySet<string>
  onClaim: (item: MarketplaceItem) => void
}) {
  return (
    <div className="flex min-h-[400px] flex-1 items-center overflow-x-auto overflow-y-hidden px-[42px] py-5">
      <div className="grid h-full max-h-[436px] grid-flow-col grid-rows-2 gap-5">
        {items.map((item) => (
          <ItemCard
            key={item.reward_id}
            item={item}
            balance={balance}
            claimed={claimed.has(item.reward_id)}
            onClaim={onClaim}
          />
        ))}
      </div>
    </div>
  )
}

/** Which step of the claim the cashier is in.
 *
 * `link` only appears for a reward the merchant never mapped to a POS discount, and hands straight
 * over to `confirm` once linked — the cashier's intent was to claim, not to do configuration
 * (Figma 2260:369: "utilisée juste après"). */
type ClaimStep =
  | { kind: 'none' }
  | { kind: 'link'; item: MarketplaceItem }
  | { kind: 'confirm'; item: MarketplaceItem; revoke: boolean; lines: PosAccountLine[] }
  | { kind: 'pickLine'; item: MarketplaceItem; lines: PosAccountLine[] }
  | { kind: 'keypad' }

/** The claim's interactive steps. One of these is on screen at a time, so they live together:
 * confirm → (which line?) → done, with the link dialog standing in when the merchant never mapped
 * the reward to a POS discount. */
function ClaimSteps({
  step,
  balance,
  busy,
  discounts,
  onConfirm,
  onRevoke,
  onPickLine,
  onLink,
  onCancel,
}: {
  step: ClaimStep
  balance: number
  busy: boolean
  discounts: PosDiscount[] | null
  onConfirm: (item: MarketplaceItem, mode: ClaimMode, lines: PosAccountLine[]) => void
  onRevoke: (item: MarketplaceItem) => void
  onPickLine: (item: MarketplaceItem, line: PosAccountLine) => void
  onLink: (item: MarketplaceItem, link: { posItemId: string; discountCode: string }) => void
  onCancel: () => void
}) {
  if (step.kind === 'confirm') {
    return (
      <ClaimConfirmDialog
        item={step.item}
        balance={balance}
        revoke={step.revoke}
        canOffer={step.lines.length > 0}
        busy={busy}
        onConfirm={(mode) => onConfirm(step.item, mode, step.lines)}
        onRevoke={() => onRevoke(step.item)}
        onCancel={onCancel}
      />
    )
  }
  if (step.kind === 'pickLine') {
    return (
      <OrderLinePicker
        item={step.item}
        lines={step.lines}
        onPick={(line) => onPickLine(step.item, line)}
        onCancel={onCancel}
      />
    )
  }
  if (step.kind === 'link' && discounts) {
    return (
      <RewardLinkDialog
        item={step.item}
        menu={FIXTURE_MENU}
        discounts={discounts}
        onLink={(link) => onLink(step.item, link)}
        onCancel={onCancel}
        // "Je ne trouve pas le bon rabais" — abandon the claim rather than invent a discount. The
        // merchant has to create one in the back office; the design's own name for this frame is
        // "fix + abandon".
        onNoSuitableDiscount={onCancel}
      />
    )
  }
  return null
}

/** A single-message dialog. The `notice` and `error` cases differ only in title and wording —
 * deliberately kept distinct, because reporting a successful undo under "something went wrong" is
 * what made a working undo read as a failure. */
function MessageDialog({
  title,
  body,
  cta,
  warn = false,
  onClose,
}: {
  title: string
  body: string | null
  cta: string
  /** Draw attention: something is still wrong on the ticket until the cashier acts. */
  warn?: boolean
  onClose: () => void
}) {
  if (!body) return null
  return (
    <Dialog title={title} onDismiss={onClose}>
      {warn && (
        // Decorative: the title and body already carry the meaning, so announcing "warning" again
        // would just make a screen reader repeat itself.
        <div aria-hidden="true" className="mt-6 text-[44px] leading-none">
          ⚠️
        </div>
      )}
      <p className="mt-6 text-[19px] text-ink-soft">{body}</p>
      <button
        type="button"
        onClick={onClose}
        className="mt-10 w-full rounded-lg bg-brand py-[18px] text-[21px] font-semibold text-white"
      >
        {cta}
      </button>
    </Dialog>
  )
}

/** Linked-customer panel for the points model (Figma 2264:2144, "v3 · 2B — Linked Panel · Grid").
 *
 * Replaces the stamp-card progress screen: a points balance plus a marketplace of menu items the
 * customer can claim right now. Points are attributed automatically on payment by the order-closed
 * webhook, so this screen never awards anything — hence the subline; the cashier's only actions
 * are claiming a reward, adding points manually, or ending the session.
 *
 * Claiming WRITES TO THE POS, both ways: it either discounts a line already on the ticket, or
 * rings the product up (`pos_addItemToCurrentAccount`) and discounts the line that appears. Either
 * way the points move only once the sale is confirmed cheaper — every POS call here is verified by
 * re-reading the account, because the add call has no callback at all and the discount toggle
 * resolves even when it discounts nothing.
 *
 * REMOVAL is not possible and is not going to be: the POS injects 39 `pos_*` functions and none of
 * them removes or voids a line (checked against a device's own runtime list, and confirmed
 * independently). So undoing a reward we rang up tells the cashier plainly to take the product off
 * the ticket — a settled decision, not a TODO. A product that was ALREADY on the ticket is never
 * touched beyond its discount.
 *
 * ponytail: the balance and the catalogue are still fixtures (`/customer` has no points shape —
 * TechSpec B2), and the claim is not yet recorded against the backend (`/pending`, B3).
 */
export function MarketplaceScreen({
  onClose,
  onDone,
}: {
  onClose: () => void
  onDone: () => void
}) {
  // `claimed` is a Set, not an array: it is what "the rewards claimed in this sale" actually is
  // (membership, no duplicates), and it makes the per-card lookup below O(1) instead of
  // rescanning the list once per card rendered.
  // Declared before the restore effect below, which reports through it.
  const posthog = usePostHog()


  // Ordered ONCE, against the balance the customer walked in with. Re-sorting on every claim
  // makes cards jump under the cashier's finger mid-service — a claim that drops another reward
  // below affordable would slide the whole grid, and the next tap lands on the wrong item.
  // Position stays put; the card's own appearance carries the state change.
  const [ordered, setOrdered] = useState(() =>
    orderForDisplay(FIXTURE_ITEMS, FIXTURE_CUSTOMER.points_balance),
  )

  // Which step of the claim the cashier is in. `link` only appears for a reward the merchant
  // never mapped to a POS discount; it hands straight over to `confirm` once linked, because the
  // cashier's intent was to claim, not to do configuration (Figma 2260:369: "utilisée juste après").
  const [step, setStep] = useState<ClaimStep>({ kind: 'none' })
  const [error, setError] = useState<string | null>(null)

  // The merchant's discount codes, read from the POS when the link dialog needs them — at that
  // moment rather than on mount, so a code added in the back office mid-service is picked up.
  const [discounts, setDiscounts] = useState<PosDiscount[] | null>(null)

  const { wallet, setWallet, appliedLines, addedLines, persistClaims } = useSaleClaims(
    posthog,
    setError,
  )
  const { balance } = wallet

  // Separate from `error`: something the cashier must DO on the till after a successful action.
  // Rendering it as an error made a working undo read as a failure.
  const [notice, setNotice] = useState<string | null>(null)

  // Ringing up and then discounting is two round-trips with a poll between them, long enough for a
  // second tap to land — so the confirm dialog's buttons disable while it runs.
  const [busy, setBusy] = useState(false)

  // The customer's REAL name, from the `/customer` call ScanFlow already made — TanStack Query
  // serves it from cache on the same key, so this costs no extra request. Falls back to the demo
  // name when there is no backend (plain browser, or an id that resolved to nothing).
  //
  // Only the name is real: the balance and the marketplace are still fixtures, because `/customer`
  // answers in the stamp model and has no points shape yet (TechSpec B2).
  const { scannedQr } = useSession()
  const customer = useCustomer(scannedQr)
  const displayName = customer.data?.consumer.display_name ?? FIXTURE_CUSTOMER.display_name

  /** Open the confirm dialog, having first resolved which lines of the open order this reward's
   * product sits on. Resolved HERE rather than after the cashier commits: the dialog uses it to
   * decide whether "Offrir" is even a real option, and offering an action that cannot happen is
   * how you get a tap that has to be undone mid-service. */
  async function openConfirm(item: MarketplaceItem) {
    let lines: PosAccountLine[] = []
    try {
      const account = await getLightspeedApi().getCurrentAccount()
      lines = matchingOrderLines(account.lines ?? [], item.pos_link?.pos_item_id)
    } catch {
      // No readable sale (or no POS at all): fall back to offering only "add", which always works.
    }
    setStep({ kind: 'confirm', item, revoke: false, lines })
  }

  async function startClaim(item: MarketplaceItem) {
    // Already claimed → tapping again offers to undo it. No order lookup needed: undo gives points
    // back, it does not touch the ticket.
    if (wallet.claimed.has(item.reward_id)) {
      setStep({ kind: 'confirm', item, revoke: true, lines: [] })
      return
    }
    if (item.pos_link) {
      await openConfirm(item)
      return
    }
    // Fail loud: with no discount codes there is nothing to link a reward to, and silently opening
    // an empty picker would read as "the merchant configured nothing" rather than "we couldn't ask".
    try {
      setDiscounts(await getLightspeedApi().getAvailableDiscounts())
      setStep({ kind: 'link', item })
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  function linkReward(item: MarketplaceItem, link: { posItemId: string; discountCode: string }) {
    // Persist the mapping onto the reward so a second claim in the same session doesn't ask again.
    const linked: MarketplaceItem = {
      ...item,
      pos_link: { pos_item_id: link.posItemId, discount_code: link.discountCode },
    }
    setOrdered((items) => items.map((i) => (i.reward_id === item.reward_id ? linked : i)))
    void openConfirm(linked)
  }

  // "Offrir" discounts a line that is ALREADY on the ticket, so it first has to resolve which one.
  // The order can hold several lines of the same item, and they are not interchangeable — the
  // cashier picks.
  //
  // "Ajouter" rings the product up first (`pos_addItemToCurrentAccount`) and then discounts the
  // line that appears — nothing to disambiguate, since we discount the line we just created.
  async function confirmClaim(item: MarketplaceItem, mode: ClaimMode, lines: PosAccountLine[]) {
    // "Offrir" onto several identical lines is the one case that still needs the cashier: they
    // are distinct discount targets with potentially different amounts.
    if (mode === 'offer' && lines.length > 1) {
      setStep({ kind: 'pickLine', item, lines })
      return
    }
    if (mode === 'add') {
      await addThenClaim(item)
      return
    }
    await claimOnLine(item, lines[0])
  }

  /** Ring the product up, then give it. Two POS writes where "Offrir" does one, so it is the path
   * that can half-succeed: if the item lands but the discount does not, the customer is looking at
   * a bill that just went UP. That case keeps the points unspent and names the added line, because
   * the API has no remove-item call — only the cashier can void it. */
  async function addThenClaim(item: MarketplaceItem) {
    const sku = item.pos_link?.pos_item_id
    if (!sku) return
    setBusy(true)
    try {
      const line = await ringUpForReward(posthog, item, sku)
      addedLines.current[item.reward_id] = true
      await claimOnLine(item, line, { added: true })
    } catch (e) {
      setError(errorMessage(e))
      setStep({ kind: 'none' })
    } finally {
      setBusy(false)
    }
  }

  /** Discount the chosen line, then spend the points and remember WHERE it landed — undo has to
   * take the discount off the same line it was put on, and a line id is the only handle for that. */
  async function claimOnLine(
    item: MarketplaceItem,
    line: PosAccountLine,
    { added = false }: { added?: boolean } = {},
  ) {
    try {
      await offerOnLine(item, line.lineId)
    } catch (e) {
      // An item rung up for a reward that then failed to discount is now a chargeable line the
      // cashier did not intend. Say which one, since we cannot take it back off ourselves.
      setError(
        added
          ? `${errorMessage(e)} ${t('voidAddedLine').replace('{item}', line.name ?? item.label)}`
          : errorMessage(e),
      )
      setStep({ kind: 'none' })
      return
    }
    appliedLines.current[item.reward_id] = line.lineId
    // Computed outside the updater: writing to the POS from inside one is a side effect React is
    // free to run twice, which would persist a stale set.
    const next = applyClaim(wallet, item)
    setWallet(next)
    persistClaims(next.claimed)
    setStep({ kind: 'none' })
  }

  // Undo: take the discount back OFF the sale before giving the points back. `pos_toggleDiscount`
  // is a toggle, so the same call that applied it removes it. If the POS refuses, the points stay
  // spent and the cashier is told — a reward marked unclaimed while the bill is still discounted
  // is the one outcome that loses the merchant money silently.
  async function undoClaim(item: MarketplaceItem) {
    const lineId = appliedLines.current[item.reward_id]
    const wasAdded = !!addedLines.current[item.reward_id]
    let failure: string | undefined
    setBusy(true)
    if (lineId) {
      try {
        const ls = getLightspeedApi()
        const code = item.pos_link?.discount_code ?? BADGER_FREE_DISCOUNT_CODE
        await ls.toggleLineDiscount(code, lineId)
        // VERIFIED, not assumed — the apply path re-reads and so must this one. `pos_toggleDiscount`
        // resolves on acceptance, so a discount that stayed on would leave the reward marked
        // unclaimed while the line is still free: the merchant gives it away and the customer keeps
        // the points.
        const after = await ls.getCurrentAccount()
        if (lineHasDiscount(after.lines?.find((l) => l.lineId === lineId))) {
          failure = t('discountStillApplied')
        }
      } catch (e) {
        failure = errorMessage(e)
      }
    }
    try {
      posthog?.capture('webext_undo_claim', {
        reward_id: item.reward_id,
        line_id: lineId,
        was_added: wasAdded,
        failed: !!failure,
        failure,
      })
    } finally {
      // In a finally so an analytics failure cannot strand the confirm dialog disabled forever.
      setBusy(false)
    }
    if (failure) {
      // Points stay spent: the sale is still discounted, so the reward really is still used.
      setError(failure)
      setStep({ kind: 'none' })
      return
    }
    delete appliedLines.current[item.reward_id]
    const next = revokeClaim(wallet, item)
    setWallet(next)
    // Persist WITHOUT this reward, so a reopen doesn't resurrect the one just cancelled.
    persistClaims(next.claimed)
    setStep({ kind: 'none' })
    // The undo SUCCEEDED; the product we rang up is simply still on the ticket at full price,
    // because the injected API has no remove-item call (verified against the device's own function
    // list). So this is an instruction, not a failure — showing it as an error made a working undo
    // look broken.
    if (wasAdded) {
      delete addedLines.current[item.reward_id]
      setNotice(t('voidAddedLine').replace('{item}', item.label))
    }
  }

  // Full-screen step, not a dialog — it replaces the panel rather than sitting over it.
  if (step.kind === 'keypad') {
    return (
      <KeypadScreen
        customerName={displayName}
        onAttribute={(points) => {
          setWallet((w) => ({ ...w, balance: w.balance + points }))
          setStep({ kind: 'none' })
        }}
        onCancel={() => setStep({ kind: 'none' })}
      />
    )
  }

  return (
    // At the POS's 1194×834 everything fits and nothing scrolls. Below that the fixed chrome
    // (host header, name, balance, footer) alone eats ~420px, so the marketplace is given a floor
    // and the screen scrolls rather than crushing the cards into unreadable slivers.
    <div className="relative flex h-full flex-col overflow-y-auto bg-canvas">
      {/* The Lightspeed host header overlays our top ~60px and cannot be removed, so the design's
       * 72px title bar is grown by --ls-safe-top and its content pushed below the line. Revert by
       * dropping the padding if the host ever stops overlaying. */}
      <header className="flex shrink-0 items-center justify-between border-b-[1.5px] border-line bg-white px-10 pt-[var(--ls-safe-top)] pb-0 h-[calc(72px+var(--ls-safe-top))]">
        <h1 className="text-[22px] font-semibold text-ink">{t('customer')}</h1>
        <img src="/brand/badger-wordmark.png" alt="Badger" className="h-[30px] w-auto" />
      </header>

      <CustomerBand displayName={displayName} balance={balance} />

      <MarketplaceGrid
        items={ordered}
        balance={balance}
        claimed={wallet.claimed}
        onClaim={startClaim}
      />

      <MarketplaceFooter
        onClose={onClose}
        onAddPoints={() => setStep({ kind: 'keypad' })}
        onDone={onDone}
      />

      <ClaimSteps
        step={step}
        balance={balance}
        busy={busy}
        discounts={discounts}
        onConfirm={(item, mode, lines) => void confirmClaim(item, mode, lines)}
        onRevoke={(item) => void undoClaim(item)}
        onPickLine={(item, line) => void claimOnLine(item, line)}
        onLink={linkReward}
        onCancel={() => setStep({ kind: 'none' })}
      />

      <MessageDialog
        title={t('actionOnTill')}
        body={notice}
        cta={t('done')}
        warn
        onClose={() => setNotice(null)}
      />
      <MessageDialog title={t('somethingWrong')} body={error} cta={t('close')} onClose={() => setError(null)} />
    </div>
  )
}
